import { T, useI18n } from '../lib/i18n'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertCircle, Inbox, LoaderCircle, X } from 'lucide-react'
import type { ReactNode } from 'react'

export function LoadingScreen({ label = 'Loading your workspace…' }: { label?: string }) {
  const { t } = useI18n()
  return (
    <div className="loading-screen" role="status">
      <div className="loading-mark"><LoaderCircle className="spin" size={26} /></div>
      <strong>{t(label)}</strong>
    </div>
  )
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'medium',
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  size?: 'small' | 'medium' | 'large'
}) {
  const { t } = useI18n()
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onClose}>
          <motion.section
            className={`modal modal--${size}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            initial={{ opacity: 0, y: 18, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.99 }}
            transition={{ duration: 0.18 }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="modal__header">
              <div>
                <h2 id="modal-title">{t(title)}</h2>
                {description && <p>{t(description)}</p>}
              </div>
              <button className="icon-button" type="button" onClick={onClose} aria-label={t('Close')}>
                <X size={20} />
              </button>
            </header>
            <div className="modal__body">{children}</div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon = <Inbox size={26} />,
  compact = false,
}: {
  title: string
  description: string
  action?: ReactNode
  icon?: ReactNode
  compact?: boolean
}) {
  const { t } = useI18n()
  return (
    <div className={`empty-state ${compact ? 'empty-state--compact' : ''}`}>
      <div className="empty-state__icon">{icon}</div>
      <h3>{t(title)}</h3>
      <p>{t(description)}</p>
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  )
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="error-state" role="alert">
      <AlertCircle size={24} />
      <div><strong><T>{"We couldn’t load this"}</T></strong><p>{message}</p></div>
      {retry && <button type="button" className="button button--secondary" onClick={retry}><T>{"Try again"}</T></button>}
    </div>
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string
  title: string
  description?: string
  actions?: ReactNode
}) {
  const { t } = useI18n()
  return (
    <header className="page-header">
      <div>
        {eyebrow && <span className="eyebrow">{t(eyebrow)}</span>}
        <h1>{t(title)}</h1>
        {description && <p>{t(description)}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  )
}

export function StatusBadge({ label, tone = 'neutral' }: { label: string; tone?: string }) {
  const { t } = useI18n()
  return <span className={`status-badge status-badge--${tone}`}>{t(label)}</span>
}

export function FieldError({ children }: { children?: ReactNode }) {
  if (!children) return null
  return <span className="field-error">{children}</span>
}
